/**
 * Human-readable names for the `call_type` values stored in `email_logs` and
 * `whatsapp_logs`. The raw value is still shown next to the label so admins
 * can match rows to backend logs; unknown future types fall back to a
 * de-snake-cased version rather than rendering `some_new_type`.
 */

const EMAIL_TYPE_LABELS: Record<string, string> = {
  otp_email: "Verification code (OTP)",
  welcome: "Welcome email",
  forgot_password: "Password reset link",
  password_reset_confirmation: "Password changed confirmation",
  admin_onboarding_alert: "Admin alert: new user awaiting verification",
  enquiry_received: "New enquiry alert to seller",
  legal_document_updated: "Terms / Privacy update notice",
  verification_success: "Profile verified notice",
  saved_search_deal_alert: "Saved-search deal match alert (not enabled)",
  transactional: "Other transactional email",
};

const WHATSAPP_TYPE_LABELS: Record<string, string> = {
  otp_whatsapp: "OTP code (login / signup)",
  // Before OTP sends got their own type, the OTP sender was the only caller
  // of this generic chatbase call — every such row is an OTP code.
  send_template_message: "OTP code (older rows)",
  send_template: "Boost deal notification",
  upsert_contact: "Sync contact to Chatbase",
  bulk_upsert_contacts: "Bulk sync contacts to Chatbase",
  upsert_lead: "Push enquiry lead to Chatbase",
  bulk_upsert_leads: "Bulk push enquiry leads to Chatbase",
  voice_agent_lead_notify: "Voice agent: new lead alert",
  voice_agent_place_call: "Voice agent: place call",
};

/** `purpose` on OTP email rows — which flow asked for the code. */
const OTP_PURPOSE_LABELS: Record<string, string> = {
  login: "Login",
  signup: "Signup",
  oauth_signup: "Google / Apple signup",
  profile_email_verify: "Profile email verification",
  profile_phone_change: "Profile phone change",
};

function humanize(raw: string): string {
  const words = raw.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : raw;
}

export function emailTypeLabel(callType: string | null | undefined): string {
  if (!callType) return "—";
  return EMAIL_TYPE_LABELS[callType] ?? humanize(callType);
}

export function whatsappTypeLabel(callType: string | null | undefined): string {
  if (!callType) return "—";
  return WHATSAPP_TYPE_LABELS[callType] ?? humanize(callType);
}

export function otpPurposeLabel(purpose: string | null | undefined): string | null {
  if (!purpose) return null;
  return OTP_PURPOSE_LABELS[purpose] ?? humanize(purpose);
}
